import React from 'react';

export const LegalPage: React.FC = () => {
    return (
        <div className="page-container" style={{ maxWidth: '800px' }}>
            <section className="hero">
                <h1 className="hero-title">Legal</h1>
                <p className="hero-subtitle">Terms of Service, Privacy Policy, and Refund Policy</p>
            </section>

            <div className="vertical-layout">
                {/* Terms of Service */}
                <section className="card">
                    <h2>Terms of Service</h2>
                    <p><strong>Last Updated:</strong> November 24, 2025</p>

                    <h3>1. Acceptance of Terms</h3>
                    <p>
                        By accessing and using PolyGrid, you agree to be bound by these Terms of Service. If you do not agree to these terms, please do not use our service.
                    </p>

                    <h3>2. Service Description</h3>
                    <p>
                        PolyGrid provides Excel to XLIFF conversion tools for localization workflows. We offer both free and paid tiers with varying limits and features.
                    </p>

                    <h3>3. User Accounts</h3>
                    <p>
                        You are responsible for maintaining the security of your account and for all activities that occur under your account. You must notify us immediately of any unauthorized use.
                    </p>

                    <h3>4. Acceptable Use</h3>
                    <p>You agree not to:</p>
                    <ul>
                        <li>Use the service for any illegal purpose</li>
                        <li>Attempt to gain unauthorized access to our systems</li>
                        <li>Interfere with or disrupt the service</li>
                        <li>Use automated scripts to abuse free tier limits</li>
                    </ul>

                    <h3>5. Subscriptions and Billing</h3>
                    <p>
                        <strong>Billing Cycle:</strong> Subscriptions are billed in advance on a monthly or annual basis, depending on your selection.
                    </p>
                    <p>
                        <strong>Auto-Renewal:</strong> Your subscription will automatically renew at the end of each billing cycle unless you cancel it.
                    </p>
                    <p>
                        <strong>Cancellation:</strong> You can cancel your subscription at any time through your account settings. Cancellation will be effective at the end of the current billing period.
                    </p>
                    <p>
                        <strong>Price Changes:</strong> We reserve the right to change subscription prices. We will provide at least 30 days' notice of any price changes.
                    </p>

                    <h3>6. Service Availability</h3>
                    <p>
                        We strive to maintain high availability but do not guarantee uninterrupted access. We reserve the right to modify, suspend, or discontinue the service at any time.
                    </p>

                    <h3>7. Limitation of Liability</h3>
                    <p>
                        PolyGrid is provided "as is" without warranties of any kind. We are not liable for any damages arising from your use of the service.
                    </p>

                    <h3>8. Changes to Terms</h3>
                    <p>
                        We may update these terms at any time. Continued use of the service after changes constitutes acceptance of the new terms.
                    </p>
                </section>

                {/* Privacy Policy */}
                <section className="card">
                    <h2>Privacy Policy</h2>
                    <p><strong>Last Updated:</strong> February 1, 2026</p>

                    <h3>1. Information We Collect</h3>
                    <p><strong>Account Information:</strong> Email address and authentication details when you create an account.</p>
                    <p><strong>Usage Data:</strong> IP addresses, file sizes, and conversion counts for rate limiting and analytics.</p>
                    <p><strong>Payment Information:</strong> Processed securely through Paddle. We do not store credit card details.</p>

                    <h3>2. How We Use Your Information</h3>
                    <ul>
                        <li>To provide and improve our service</li>
                        <li>To enforce rate limits and prevent abuse</li>
                        <li>To process payments</li>
                        <li>To communicate important service updates</li>
                    </ul>

                    <h3>3. File Processing</h3>
                    <p>
                        Files you upload are processed in memory and stored temporarily during conversion. We do not permanently store your file contents. Converted files are available for download and then deleted.
                    </p>

                    <h3>4. Data Sharing</h3>
                    <p>
                        We do not sell your personal information. We may share data with:
                    </p>
                    <ul>
                        <li><strong>Paddle:</strong> For payment processing</li>
                        <li><strong>AWS:</strong> For hosting and infrastructure</li>
                        <li><strong>Legal Authorities:</strong> When required by law</li>
                    </ul>

                    <h3>5. Data Security</h3>
                    <p>
                        We use industry-standard security measures including encryption, authentication, and secure infrastructure to protect your data.
                    </p>

                    <h3>6. Your Rights</h3>
                    <p>You have the right to:</p>
                    <ul>
                        <li>Access your personal information</li>
                        <li>Request deletion of your account and data</li>
                        <li>Opt out of marketing communications</li>
                    </ul>

                    <h3>7. Cookies</h3>
                    <p>
                        We use essential cookies for authentication and session management. No third-party tracking cookies are used.
                    </p>

                    <h3>8. Contact</h3>
                    <p>
                        For privacy concerns, contact us at: <a href="mailto:support@usepolygrid.com">support@usepolygrid.com</a>
                    </p>
                </section>

                {/* Refund Policy */}
                <section className="card">
                    <h2>Refund Policy</h2>
                    <p><strong>Last Updated:</strong> November 26, 2025</p>

                    <h3>1. Subscriptions</h3>
                    <p>
                        <strong>Monthly Plans:</strong> You may cancel your monthly subscription at any time. The cancellation will take effect at the end of the current billing period. We do not offer refunds for partial months.
                    </p>
                    <p>
                        <strong>Annual Plans:</strong> Annual subscriptions are billed upfront. If you cancel, your access continues until the end of the one-year period. We generally do not offer refunds for annual plans unless required by law or in cases of technical failure preventing service use.
                    </p>

                    <h3>2. Refund Requests</h3>
                    <p>
                        Refund requests must be submitted within 14 days of purchase. Contact us at <a href="mailto:support@usepolygrid.com">support@usepolygrid.com</a> with:
                    </p>
                    <ul>
                        <li>Your account email</li>
                        <li>Transaction ID</li>
                        <li>Reason for refund request</li>
                    </ul>

                    <h3>3. Refund Processing</h3>
                    <p>
                        Approved refunds will be processed within 5-10 business days to the original payment method.
                    </p>

                    <h3>4. Free Tier</h3>
                    <p>
                        The free tier is provided at no cost and is not eligible for refunds.
                    </p>
                </section>
            </div>
        </div>
    );
};
